'use client';

import { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Button,
  LegacyDialog,
  Scroll,
  MetaDefault,
  MarkdownPreview,
} from '@terra/ui-design-system';
import { TERMS_AND_CONDITIONS_TEXT } from './TermsAndConditionsContent';
import { isTerraProxyAuthPublicPathname } from '../middlewares/auth/public-routes';
import { useSharedAppSetting } from '../hooks/useSharedAppSetting';
import { APP_SETTING_KEY } from '../electron-shared/app-settings/app-settings-keys';

const TERMS_VERSION = 'v1';
const TERMS_ACCEPTED_STORAGE_KEY = `terra-proxy-terms-accepted-${TERMS_VERSION}`;
const TERMS_URL = 'https://terra.security/tc';
const hasLocallyAcceptedTerms = (): boolean =>
  localStorage.getItem(TERMS_ACCEPTED_STORAGE_KEY) === 'true';

export function TermsAndConditionsModal() {
  const pathname = usePathname();
  const {
    status: sharedStatus,
    value: sharedAcceptedVersions,
    save: saveSharedAcceptedVersions,
  } = useSharedAppSetting(APP_SETTING_KEY.TERMS_ACCEPTED_VERSIONS);
  const [isLocallyAccepted, setIsLocallyAccepted] = useState<boolean | null>(
    null,
  );

  useEffect(() => {
    setIsLocallyAccepted(hasLocallyAcceptedTerms());
  }, []);

  const isSharedAccepted = !!sharedAcceptedVersions?.includes(TERMS_VERSION);
  const shouldShareLocalAcceptance =
    sharedStatus === 'ready' && !isSharedAccepted && !!isLocallyAccepted;

  useEffect(() => {
    if (shouldShareLocalAcceptance) {
      void saveSharedAcceptedVersions([
        ...(sharedAcceptedVersions ?? []),
        TERMS_VERSION,
      ]);
    }
  }, [
    shouldShareLocalAcceptance,
    sharedAcceptedVersions,
    saveSharedAcceptedVersions,
  ]);

  const isAuthRoute =
    isTerraProxyAuthPublicPathname(pathname) || pathname.startsWith('/e2e');
  const isAcceptanceKnown =
    isLocallyAccepted !== null && sharedStatus !== 'loading';
  const isOpen =
    !isAuthRoute &&
    isAcceptanceKnown &&
    !isSharedAccepted &&
    !isLocallyAccepted;

  const handleAccept = useCallback(() => {
    localStorage.setItem(TERMS_ACCEPTED_STORAGE_KEY, 'true');
    setIsLocallyAccepted(true);
  }, []);

  const handleOpenExternalTerms = useCallback(() => {
    void window.electronAPI?.shell?.openExternal(TERMS_URL);
  }, []);

  if (!isOpen) {
    return null;
  }

  return (
    <LegacyDialog.Root open={isOpen} modal>
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content
          maxW="900px"
          w="calc(100vw - 48px)"
          h="calc(100vh - 48px)"
          maxH="900px"
        >
          <VStack alignItems="stretch" gap={4} p={4} h="full">
            <VStack alignItems="flex-start" gap={1}>
              <LegacyDialog.Title>Terms and Conditions</LegacyDialog.Title>
              <LegacyDialog.Description>
                You must review and accept the terms before first use.
              </LegacyDialog.Description>
            </VStack>

            <Box
              flex="1"
              borderWidth="1px"
              borderColor="border.default"
              borderRadius="sm"
              overflow="hidden"
            >
              <Scroll orientation="vertical" h="full">
                <Box p={4}>
                  <MarkdownPreview source={TERMS_AND_CONDITIONS_TEXT} />
                </Box>
              </Scroll>
            </Box>

            <HStack justifyContent="space-between" alignItems="center">
              <MetaDefault color="fg.muted">Showing bundled terms</MetaDefault>
              <HStack gap={2}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenExternalTerms}
                >
                  Open terms in browser
                </Button>
                <Button size="sm" onClick={handleAccept}>
                  I Agree
                </Button>
              </HStack>
            </HStack>
          </VStack>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
